import { InboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../tailwindComponents/Button';
import {
  FulfilmentStageTracker,
  latestStageView,
} from './FulfilmentStageTracker';
import { buildInboundStages } from './inboundStages';
import {
  getInboundStatusColor,
  getStatusBadgeClasses,
} from '../utils/statusColors';

interface WarehouseClientInboundSideDrawerProps {
  selectedInboundProcess: InboundProcess | null;
  setSelectedInboundProcess: (payload: InboundProcess | null) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isDesktop: boolean;
  selectedInboundProcessHasDescription: boolean;
  setIsViewShipmentNoteDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsViewGoodsReceivedNoteDialogOpen: Dispatch<SetStateAction<boolean>>;
  selectedProcessChildType: string | null;
  setSelectedProcessChildType: (payload: string | null) => void;
}

const childTypeToStageKey: Record<string, string> = {
  shipmentNote: 'shipment',
  goodsReceivedNote: 'grn',
};

export function WarehouseClientInboundSideDrawer({
  selectedInboundProcess,
  setSelectedInboundProcess,
  isOpen,
  setIsOpen,
  isDesktop,
  selectedInboundProcessHasDescription,
  setIsViewShipmentNoteDialogOpen,
  setIsViewGoodsReceivedNoteDialogOpen,
  selectedProcessChildType,
  setSelectedProcessChildType,
}: WarehouseClientInboundSideDrawerProps) {
  const onClose = () => {
    setIsOpen(false);
    setSelectedInboundProcess(null);
    setSelectedProcessChildType(null);
  };

  const getBackdropClasses = () => {
    if (!isOpen) return 'opacity-0 pointer-events-none';
    return isDesktop ? '' : 'bg-black/20';
  };

  const status = selectedInboundProcess?.status ?? '';
  const createdAt = selectedInboundProcess?.createdAt;
  const description = selectedInboundProcess?.description;
  const shipmentNote = selectedInboundProcess?.shipmentNote;
  const notices = selectedInboundProcess?.notices;

  const statusColor = getInboundStatusColor(status);

  const totalItemsQuantity =
    shipmentNote?.skus.reduce((sum, sku) => sum + sku.skuQuantity, 0) ?? 0;

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete =
    !!selectedInboundProcess?.goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;

  const closeAndOpen = (open: Dispatch<SetStateAction<boolean>>) => () => {
    setIsOpen(false);
    open(true);
  };

  const stages = selectedInboundProcess
    ? buildInboundStages(selectedInboundProcess, {
        onViewShipment: closeAndOpen(setIsViewShipmentNoteDialogOpen),
        onViewGoodsReceivedNote: closeAndOpen(
          setIsViewGoodsReceivedNoteDialogOpen
        ),
      })
    : [];

  const footerView = latestStageView(stages) ?? {
    label: 'View Shipment Note',
    onView: closeAndOpen(setIsViewShipmentNoteDialogOpen),
  };

  const initialExpandedKey = selectedProcessChildType
    ? childTypeToStageKey[selectedProcessChildType] ?? null
    : null;

  return (
    <>
      {/* Backdrop - only visible on mobile */}
      <div
        className={`fixed inset-0 z-drawerBackdrop transition-opacity duration-300 ${getBackdropClasses()}`}
        onClick={onClose}
        role='presentation'
      />

      {/* Drawer */}
      <div
        className={`fixed z-drawer bg-white shadow-xl flex flex-col overflow-hidden will-change-transform transition-transform duration-300 ease-out ${
          isDesktop
            ? `right-0 top-0 h-full w-[22rem] border-l border-gray-200 ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
            : `bottom-0 left-0 right-0 max-h-[90vh] rounded-t-2xl ${
                isOpen ? 'translate-y-0' : 'translate-y-full'
              }`
        }`}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-5 py-4 bg-white'>
          <h2 className='text-xl font-semibold text-gray-900'>Shipment Note</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1 rounded hover:bg-gray-100 text-gray-400 transition-colors'
            aria-label='Close'
          >
            <XMarkIcon className='w-5 h-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-5 py-4'>
          {/* Status Badge */}
          <span className={`${getStatusBadgeClasses(statusColor)} mb-6`}>
            {status}
          </span>

          {/* Basic Details Section */}
          <div className='bg-gray-50 rounded-lg p-4 mb-6'>
            <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
              Basic Details
            </p>

            <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {shipmentNote?.supplier.name}
                </p>
                <p className='text-xs text-gray-400'>Supplier</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {createdAt ? dayjs(createdAt).format('DD/MM/YY') : '-'}
                </p>
                <p className='text-xs text-gray-400'>Creation date</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {shipmentNote?.shipmentNoteNumber}
                </p>
                <p className='text-xs text-gray-400'>Shipment Note #</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {shipmentNote?.estimatedTimeOfArrival
                    ? dayjs
                        .utc(shipmentNote.estimatedTimeOfArrival.dateTime)
                        .tz(shipmentNote.estimatedTimeOfArrival.timeZone)
                        .format('DD/MM/YY')
                    : '-'}
                </p>
                <p className='text-xs text-gray-400'>
                  Estimated Time of Arrival
                </p>
              </div>
              <div data-testid='sku-count'>
                <p className='text-sm font-semibold text-gray-900'>
                  {shipmentNote?.skus.length ?? 0}
                </p>
                <p className='text-xs text-gray-400'>SKUs</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {totalItemsQuantity}
                </p>
                <p className='text-xs text-gray-400'>Total Qty (Items)</p>
              </div>
            </div>
          </div>

          {/* Description */}
          {selectedInboundProcessHasDescription && (
            <div className={hasIssues ? 'mb-4' : 'mb-6'}>
              <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
                Description
              </p>
              <p className='text-sm text-gray-700'>{description}</p>
            </div>
          )}

          {/* Issues */}
          {hasIssues && (
            <div className='mb-6'>
              <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
                Issues
              </p>
              <div className='space-y-2'>
                {notices?.map((notice) => (
                  <div
                    key={notice}
                    className={`flex items-center gap-2 p-2 rounded ${
                      hasIssuesBeforeProcessIsComplete
                        ? 'bg-amber-50'
                        : 'bg-red-50'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      className={`w-4 h-4 flex-shrink-0 ${
                        hasIssuesBeforeProcessIsComplete
                          ? 'text-amber-600'
                          : 'text-red-600'
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        hasIssuesBeforeProcessIsComplete
                          ? 'text-amber-800'
                          : 'text-red-800'
                      }`}
                    >
                      {notice}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fulfilment Progress */}
          {selectedInboundProcess && (
            <FulfilmentStageTracker
              key={selectedInboundProcess.id}
              stages={stages}
              initialExpandedKey={initialExpandedKey}
              onCollapse={() => setSelectedProcessChildType(null)}
            />
          )}
        </div>

        {/* Footer */}
        <div className='flex gap-3 px-5 py-4 border-t border-gray-100 bg-white'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors'
          >
            Cancel
          </button>
          <Button
            label={footerView.label}
            intent='primary'
            size='sm'
            className='flex-1'
            onClick={footerView.onView}
          />
        </div>
      </div>
    </>
  );
}
